/**
 * Event Types System for Vietnamese Cultural Events
 * 8 event types: Lễ Tết, Giỗ, Cưới Hỏi, Ma Chay, Sinh Nhật, Kỷ Niệm, Công Việc, Khác
 */

export enum EventType {
    LE_TET = 'le_tet',
    GIO = 'gio',
    CUOI_HOI = 'cuoi_hoi',
    MA_CHAY = 'ma_chay',
    SINH_NHAT = 'sinh_nhat',
    KY_NIEM = 'ky_niem',
    CONG_VIEC = 'cong_viec',
    KHAC = 'khac',
}

export interface EventField {
    name: string;
    label: string;
    type: 'text' | 'textarea' | 'number' | 'select' | 'date' | 'checkbox';
    required?: boolean;
    placeholder?: string;
    options?: string[];
    defaultValue?: unknown;
}

export interface EventTypeConfig {
    id: EventType;
    label: string;
    labelEn: string;
    icon: string;
    color: string;
    bgColor: string;
    description: string;
    fields: EventField[];
    defaultReminders: number[];
}

export const EVENT_TYPE_CONFIGS: Record<EventType, EventTypeConfig> = {
    [EventType.LE_TET]: {
        id: EventType.LE_TET,
        label: 'Lễ Tết',
        labelEn: 'Holiday/Festival',
        icon: '🏮',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        description: 'Các ngày lễ, tết truyền thống',
        fields: [
            {
                name: 'festivalName',
                label: 'Tên lễ',
                type: 'select',
                required: true,
                options: [
                    'Tết Nguyên Đán',
                    'Tết Trung Thu',
                    'Tết Đoan Ngọ',
                    'Rằm tháng Giêng',
                    'Rằm tháng 7',
                    'Tết Hàn Thực',
                    'Vu Lan',
                    'Khác',
                ],
            },
            {
                name: 'preparations',
                label: 'Chuẩn bị',
                type: 'textarea',
                placeholder: 'Cần mua gì, chuẩn bị gì...',
            },
            {
                name: 'location',
                label: 'Địa điểm',
                type: 'text',
                placeholder: 'Nhà, chùa, đình...',
            },
        ],
        defaultReminders: [7, 3, 1],
    },

    [EventType.GIO]: {
        id: EventType.GIO,
        label: 'Giỗ',
        labelEn: 'Death Anniversary',
        icon: '🕯️',
        color: 'text-gray-700',
        bgColor: 'bg-gray-100',
        description: 'Ngày giỗ người thân',
        fields: [
            {
                name: 'relationship',
                label: 'Quan hệ',
                type: 'select',
                required: true,
                options: [
                    'Ông nội', 'Bà nội', 'Ông ngoại', 'Bà ngoại',
                    'Cha', 'Mẹ', 'Chú', 'Bác', 'Cô', 'Dì',
                    'Anh', 'Chị', 'Em', 'Khác',
                ],
            },
            {
                name: 'deathYear',
                label: 'Năm mất',
                type: 'number',
                placeholder: 'VD: 2020',
            },
            {
                name: 'location',
                label: 'Địa điểm tổ chức',
                type: 'text',
                placeholder: 'Nhà riêng, chùa...',
            },
            {
                name: 'attendees',
                label: 'Số lượng khách',
                type: 'number',
                placeholder: 'Ước tính số người',
            },
            {
                name: 'preparations',
                label: 'Chuẩn bị',
                type: 'textarea',
                placeholder: 'Mâm cỗ, hương đèn, cúng phẩm...',
            },
        ],
        defaultReminders: [7, 3, 1],
    },

    [EventType.CUOI_HOI]: {
        id: EventType.CUOI_HOI,
        label: 'Đám Cưới',
        labelEn: 'Wedding',
        icon: '💒',
        color: 'text-pink-600',
        bgColor: 'bg-pink-50',
        description: 'Đám cưới',
        fields: [
            {
                name: 'relationship',
                label: 'Quan hệ với bạn',
                type: 'select',
                options: ['Bạn thân', 'Đồng nghiệp', 'Họ hàng', 'Người thân', 'Khác'],
            },
            {
                name: 'location',
                label: 'Địa điểm',
                type: 'text',
                required: true,
                placeholder: 'Nhà hàng, trung tâm tiệc cưới...',
            },
            {
                name: 'giftAmount',
                label: 'Mừng cưới (VNĐ)',
                type: 'number',
                placeholder: 'Số tiền',
            },
            {
                name: 'dresscode',
                label: 'Dress code',
                type: 'text',
                placeholder: 'Áo dài, vest, lịch sự...',
            },
        ],
        defaultReminders: [14, 7, 3, 1],
    },

    [EventType.MA_CHAY]: {
        id: EventType.MA_CHAY,
        label: 'Ma Chay',
        labelEn: 'Funeral',
        icon: '🖤',
        color: 'text-gray-900',
        bgColor: 'bg-gray-200',
        description: 'Tang lễ, lễ cúng',
        fields: [
            {
                name: 'relationship',
                label: 'Quan hệ',
                type: 'text',
                required: true,
            },
            {
                name: 'ceremonyType',
                label: 'Loại lễ',
                type: 'select',
                options: ['Tang lễ', 'Lễ 49 ngày', 'Lễ 100 ngày', 'Giỗ đầu', 'Khác'],
            },
            {
                name: 'location',
                label: 'Địa điểm',
                type: 'text',
                required: true,
            },
            {
                name: 'condolenceMoney',
                label: 'Phúng điếu (VNĐ)',
                type: 'number',
                placeholder: 'Số tiền',
            },
        ],
        defaultReminders: [3, 1],
    },

    [EventType.SINH_NHAT]: {
        id: EventType.SINH_NHAT,
        label: 'Sinh Nhật',
        labelEn: 'Birthday',
        icon: '🎂',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        description: 'Sinh nhật người thân, bạn bè',
        fields: [
            {
                name: 'personName',
                label: 'Tên',
                type: 'text',
                required: true,
            },
            {
                name: 'relationship',
                label: 'Quan hệ',
                type: 'text',
            },
            {
                name: 'lunarBirthday',
                label: 'Sinh nhật Âm lịch',
                type: 'checkbox',
                defaultValue: false,
            },
            {
                name: 'age',
                label: 'Tuổi',
                type: 'number',
            },
            {
                name: 'gift',
                label: 'Quà tặng',
                type: 'text',
                placeholder: 'Ý tưởng quà...',
            },
        ],
        defaultReminders: [7, 1],
    },

    [EventType.KY_NIEM]: {
        id: EventType.KY_NIEM,
        label: 'Kỷ Niệm',
        labelEn: 'Anniversary',
        icon: '💝',
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        description: 'Ngày kỷ niệm đặc biệt',
        fields: [
            {
                name: 'eventName',
                label: 'Tên sự kiện',
                type: 'text',
                required: true,
                placeholder: 'Kỷ niệm ngày cưới, hẹn hò...',
            },
            {
                name: 'yearsSince',
                label: 'Năm thứ',
                type: 'number',
            },
            {
                name: 'plans',
                label: 'Kế hoạch',
                type: 'textarea',
                placeholder: 'Đi ăn, du lịch...',
            },
        ],
        defaultReminders: [7, 3, 1],
    },

    [EventType.CONG_VIEC]: {
        id: EventType.CONG_VIEC,
        label: 'Công Việc',
        labelEn: 'Work',
        icon: '💼',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        description: 'Sự kiện công việc',
        fields: [
            {
                name: 'taskName',
                label: 'Công việc',
                type: 'text',
                required: true,
            },
            {
                name: 'priority',
                label: 'Độ ưu tiên',
                type: 'select',
                options: ['Cao', 'Trung bình', 'Thấp'],
            },
            {
                name: 'description',
                label: 'Mô tả',
                type: 'textarea',
            },
        ],
        defaultReminders: [1],
    },

    [EventType.KHAC]: {
        id: EventType.KHAC,
        label: 'Khác',
        labelEn: 'Other',
        icon: '📝',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        description: 'Sự kiện khác',
        fields: [],
        defaultReminders: [1],
    },
};

// Helper functions
export const getEventTypeConfig = (type: EventType): EventTypeConfig => {
    return EVENT_TYPE_CONFIGS[type];
};

export const getEventTypeLabel = (type: EventType): string => {
    return EVENT_TYPE_CONFIGS[type]?.label || 'Khác';
};

export const getEventTypeIcon = (type: EventType): string => {
    return EVENT_TYPE_CONFIGS[type]?.icon || '📝';
};

export const getEventTypeColor = (type: EventType): string => {
    return EVENT_TYPE_CONFIGS[type]?.color || 'text-gray-600';
};

export const getEventTypeBgColor = (type: EventType): string => {
    return EVENT_TYPE_CONFIGS[type]?.bgColor || 'bg-gray-50';
};

export const getAllEventTypes = (): EventType[] => {
    return Object.values(EventType);
};
